import { apiErrorCode } from '@frontend/features/travel-services/queries';
import { readHotelResultWindow } from './hotelResultCollection';
import type { HotelExpansion } from '@bandido/shared/types/hotels/hotel-search-progress';
import type {
  HotelContentBatch,
  HotelContentLanguage,
  HotelPublicContent,
} from '@bandido/shared/types/hotels/hotel-content';
import type {
  HotelResultCard,
  HotelListOptions,
  HotelResultGroup,
  HotelResultPage,
} from '@bandido/shared/types/hotels/hotel-search-results';
import type { HotelSearchFilters } from '@bandido/shared/types/hotels/hotel-search-filters';
import {
  HotelFare,
  HotelSearchDraft,
  HotelSearchState,
  HotelSummary,
  hotelSearchCriteria,
  shouldPollHotels,
  normalizedHotelFilters,
} from './searchModel';

export interface HotelCatalogPage {
  hotels: HotelSummary[];
  nextOffset: number | null;
  searchContextId?: string | null;
}
export interface HotelSearchTransport {
  expansion(id: string, hotel: string, signal: AbortSignal, create?: boolean): Promise<HotelExpansion>;
  contentSummaries(ids: string[], language: HotelContentLanguage, signal: AbortSignal): Promise<HotelContentBatch>;
  content(id: string, language: HotelContentLanguage, signal: AbortSignal): Promise<HotelPublicContent>;
  resolve(draft: HotelSearchDraft, offset: number, signal: AbortSignal): Promise<HotelCatalogPage>;
  create(
    criteria: ReturnType<typeof hotelSearchCriteria> & { search_context_id: string },
    signal: AbortSignal,
  ): Promise<HotelSearchState>;
  status(id: string, signal: AbortSignal): Promise<HotelSearchState>;
  hotels(
    id: string,
    signal: AbortSignal,
    filters?: HotelSearchFilters,
    cursor?: string,
    options?: HotelListOptions,
  ): Promise<HotelResultPage<HotelResultCard>>;
  groups(
    id: string,
    hotel: string,
    signal: AbortSignal,
    filters?: HotelSearchFilters,
    cursor?: string,
  ): Promise<HotelResultPage<HotelResultGroup>>;
  hotelFares(
    id: string,
    hotel: string,
    signal: AbortSignal,
    filters?: HotelSearchFilters,
    cursor?: string,
    group?: string,
  ): Promise<HotelResultPage<{ group_id: string; fare: HotelFare }>>;
}
export interface HotelSearchSnapshot {
  phase: 'loading' | 'running' | 'complete' | 'failed' | 'expired' | 'stopped';
  hotels: HotelSummary[];
  cards: HotelResultCard[];
  resultMeta?: HotelResultPage<HotelResultCard>['meta'];
  nextOffset: number | null;
  appliedFilters?: HotelSearchFilters;
  totalCount?: number;
  search?: HotelSearchState;
}
export const emptyHotelSnapshot: HotelSearchSnapshot = { phase: 'loading', hotels: [], cards: [], nextOffset: null };

// Each await is an ownership boundary: a late response may neither publish
// into a new search nor start another request for an obsolete search.
export async function runHotelSearch({
  transport,
  draft,
  offset,
  signal,
  isCurrent,
  publish,
  pause,
  pages = () => 1,
  onPagesRead,
  onResultsProgress,
  now = Date.now,
}: {
  transport: HotelSearchTransport;
  draft: HotelSearchDraft;
  offset: number;
  signal: AbortSignal;
  isCurrent: () => boolean;
  publish: (snapshot: HotelSearchSnapshot) => void;
  pause: () => Promise<void>;
  pages?: () => number;
  onPagesRead?: (count: number) => void;
  onResultsProgress?: (loaded: number, total: number) => void;
  now?: () => number;
}) {
  const start = now();
  const current = () => !signal.aborted && isCurrent();
  if (!current()) return;
  publish({ ...emptyHotelSnapshot });
  const page = await transport.resolve(draft, offset, signal);
  if (!current()) return;
  if (!page.hotels.length) {
    publish({ phase: 'complete', hotels: [], cards: [], nextOffset: page.nextOffset });
    return;
  }
  const criteria = hotelSearchCriteria(
    draft,
    page.hotels.map((h) => h.hotel_id),
  );
  if (!page.searchContextId) throw new Error('HOTEL_SEARCH_CONTEXT_REQUIRED');
  let search = await transport.create({ ...criteria, search_context_id: page.searchContextId }, signal);
  if (!current()) return;
  // Preserve the accepted resource even when its first result view conflicts.
  // Recovery can then read this same Search rather than create provider work.
  publish({
    phase: 'running',
    hotels: page.hotels,
    cards: [],
    nextOffset: page.nextOffset,
    search,
    appliedFilters: normalizedHotelFilters(draft.filters),
  });
  await readHotelSearchResults({
    transport,
    page,
    search,
    filters: normalizedHotelFilters(draft.filters),
    options: draft.results ?? { sort: 'selection' },
    signal,
    isCurrent,
    publish,
    pause,
    startedAt: start,
    pages,
    onPagesRead,
    onResultsProgress,
    now,
  });
}

/** Read another view of the same persisted search; never creates provider work. */
export async function readHotelSearchResults({
  transport,
  page,
  search: initialSearch,
  filters,
  options = { sort: 'selection' },
  signal,
  isCurrent,
  publish,
  pause,
  startedAt,
  pages = () => 1,
  onPagesRead,
  onResultsProgress,
  now = Date.now,
}: {
  transport: HotelSearchTransport;
  page: HotelCatalogPage;
  search: HotelSearchState;
  filters: HotelSearchFilters;
  options?: HotelListOptions;
  signal: AbortSignal;
  isCurrent: () => boolean;
  publish: (snapshot: HotelSearchSnapshot) => void;
  pause: () => Promise<void>;
  startedAt: number;
  pages?: () => number;
  onPagesRead?: (count: number) => void;
  onResultsProgress?: (loaded: number, total: number) => void;
  now?: () => number;
}) {
  let search = initialSearch;
  const current = () => !signal.aborted && isCurrent();
  const id = search.search_id;
  let revisionRetries = 0;
  while (current()) {
    if (search.search_id !== id) throw new Error('Hotel search identity mismatch');
    // Fetch cards after lifecycle, so a completed status cannot be combined
    // with a card snapshot fetched before that status became terminal.
    let result: HotelResultPage<HotelResultCard>;
    try {
      result = await readHotelResultWindow(
        (requestSignal, cursor) => transport.hotels(id, requestSignal, filters, cursor, options),
        signal,
        pages,
        [],
        (card) => card.property_id,
        (count) => {
          if (current()) onPagesRead?.(count);
        },
        (loaded, total) => {
          if (current()) onResultsProgress?.(loaded, total);
        },
      );
      revisionRetries = 0;
    } catch (reason) {
      if (!current()) return;
      const changed =
        apiErrorCode(reason) === 'HOTEL_RESULTS_CHANGED' ||
        (reason instanceof Error && reason.message === 'HOTEL_RESULTS_CHANGED');
      if (!changed || ++revisionRetries > 3) throw reason;
      // Keep the last coherent view while retrying at the normal cadence, never
      // append new-revision pages to old ones or spin on 409.
      await pause();
      if (!current()) return;
      search = await transport.status(id, signal);
      continue;
    }
    const cards = result.data;
    if (!current()) return;
    if (
      result.meta.search_id !== id ||
      cards.some((card) => !page.hotels.some((hotel) => hotel.hotel_id === card.property_id))
    )
      throw new Error('Hotel fare identity mismatch');
    const polling = shouldPollHotels(search.status, now() - startedAt);
    const phase =
      search.status === 'failed'
        ? 'failed'
        : search.status === 'completed'
          ? 'complete'
          : polling
            ? 'running'
            : 'expired';
    publish({
      phase,
      hotels: page.hotels,
      cards,
      resultMeta: result.meta,
      nextOffset: page.nextOffset,
      search,
      appliedFilters: result.meta.applied_filters,
      totalCount: result.meta.total_count,
    });
    if (!polling) return;
    await pause();
    if (!current()) return;
    search = await transport.status(id, signal);
  }
}
